import { Suspense, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { useObservable } from "dexie-react-hooks"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { db, dbReadyPromise } from "@/db/schema"
import { AuthGateInteraction } from "@/features/auth"
import { InAppInteractionOverlay } from "@/features/auth/components/InAppInteractionOverlay"
import { populateIfNeeded } from "@/features/onboarding"
import { InitialSyncLoader } from "@/features/onboarding"
import { useInitialSyncReady } from "@/features/onboarding/useInitialSyncReady"
import { initDocProcessing } from "@/services/doc-processing"
import { startBackgroundTasks } from "@/background"
import { hasAnyCachedPlaceholder } from "@/features/views/loadingPlaceholders"
import { useAuthStore } from "@/features/auth/stores/authStore"
import { consumeRecoveryState } from "@/shared/utils/platform/handleIndexedDBError"
import { waitForInitialSync } from "@/shared/utils/sync/waitForInitialSync"
import { clearUserLocalStorage } from "@/features/auth/utils/logout"
import type { DXCUserInteraction } from "dexie-cloud-addon"

/** Timeout before showing "having trouble connecting" message */
const LOADING_TIMEOUT_MS = 10_000

/** Id of the "Preparing your views..." toast; the sidebar dismisses it once its data is there. */
const PREPARING_VIEWS_TOAST_ID = "preparing-views"

interface DatabaseProviderProps {
  children: ReactNode
  /**
   * Render `children` as soon as the user is signed in — no full-screen InitialSyncLoader, and
   * without waiting for `db.open()` (which blocks on the first data download after a login).
   * The sync, populate-on-"in-sync" and background-task startup still run, unchanged.
   *
   * ONLY for a route that needs the session and nothing else (the OAuth consent page): its
   * children must not mount Dexie live queries. A live query created while Dexie Cloud's sync
   * write transaction is in flight can miss its notification and stay stale until reload
   * (docs/dexie-livequery-bug-report.md) — which is exactly what the loader keeps the app away
   * from. It also never shows "Preparing your views...": nothing on such a route dismisses it.
   */
  deferInitialSync?: boolean
}

/**
 * LoadingWithTimeout — branded loading screen that shows a connection
 * problem message + retry after LOADING_TIMEOUT_MS.
 *
 * Replaces the old "breathing logo forever" with graceful degradation.
 */
function LoadingWithTimeout({
  dbOpenResolved,
  onRetry,
}: {
  dbOpenResolved: boolean
  onRetry: () => void
}) {
  const { t } = useTranslation()
  const [showTimeout, setShowTimeout] = useState(false)
  const [retrying, setRetrying] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setShowTimeout(true), LOADING_TIMEOUT_MS)
    return () => clearTimeout(timer)
  }, [])

  const handleRetry = useCallback(() => {
    setRetrying(true)
    onRetry()
    // If still stuck after 3s, allow another retry
    setTimeout(() => setRetrying(false), 3000)
  }, [onRetry])

  const handleHardReset = useCallback(() => {
    // Nuclear option: reload the page entirely
    window.location.href = "/"
  }, [])

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-6 p-4">
      <svg
        width="48"
        height="48"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="animate-breathe"
        aria-label="Loading"
      >
        <path d="M1.3335 11.0667V20.5333C1.3335 23.4667 2.8735 25.4267 4.7935 26.5333L12.3735 30.9267C14.1068 31.9267 15.4402 32.0067 16.1068 32.0067C16.7735 32.0067 18.2402 31.7 19.5668 30.9267L27.2268 26.5333C29.5268 25.2 30.6802 23.0667 30.6802 20.5333V11.0667C30.6802 8.4 29.3668 6.6667 27.5268 5.6L19.6868 1.0667C18.5268 0.4 17.0268 0 16.1002 0C15.1668 0 14.2402 0.0601 12.4935 1.0667L4.6602 5.6C2.4002 6.9067 1.3335 9.0667 1.3335 11.0667Z" fill="#FB4B97"/>
        <path d="M18.2129 31.8046L30.9607 24.4424L23.3929 11.3335L14.1282 17.7282L9.0601 16.6628L18.2129 31.8046Z" fill="url(#pl0)"/>
        <path d="M11.3908 14.6021C11.7804 14.9917 13.3978 16.7703 14.162 17.6116L20.5834 10.8498C20.954 10.4792 21.3538 10.2696 21.8722 10.2696C22.7033 10.2696 23.4746 10.9812 23.4746 11.843C23.4746 12.365 23.2552 12.789 22.9912 13.0494L15.847 21.2598C15.3576 21.7492 14.9234 21.7784 14.614 21.7784C14.3144 21.7784 13.848 21.6246 13.4776 21.2598C13.004 20.786 10.808 18.4218 9.7542 17.2946C9.338 16.8784 9.0126 16.4346 9.0126 15.8834C9.0126 14.843 9.7834 14.2546 10.616 14.2546C11.1998 14.2546 11.5934 14.5356 11.3908 14.6021Z" fill="white"/>
        <defs><linearGradient id="pl0" x1="16.8616" y1="14.7489" x2="24.5893" y2="28.1195" gradientUnits="userSpaceOnUse"><stop stopColor="white" stopOpacity="0.05"/><stop offset="1" stopColor="white" stopOpacity="0.12"/></linearGradient></defs>
      </svg>

      {showTimeout && dbOpenResolved && (
        <div className="flex flex-col items-center gap-3 animate-in fade-in duration-300">
          <p className="text-sm text-muted-foreground text-center max-w-xs">
            {t("Having trouble connecting to our servers. This could be a temporary issue.")}
          </p>
          <button
            onClick={handleRetry}
            disabled={retrying}
            className="px-6 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium active:scale-95 transition-transform disabled:opacity-50"
          >
            {retrying ? t("Connecting...") : t("Try signing in again")}
          </button>
          <button
            onClick={handleHardReset}
            className="text-xs text-muted-foreground hover:text-foreground active:text-foreground"
          >
            {t("Restart app")}
          </button>
        </div>
      )}

    </div>
  )
}

/**
 * Initializes Dexie + Cloud, handles authentication, and provides a
 * Suspense boundary for child components using useSuspendingLiveQuery.
 *
 * Auth gate pattern (from dexie-cloud-starter):
 * 1. Observe db.cloud.userInteraction — when non-null, Dexie Cloud
 *    wants to interact with the user (login, OTP, re-auth, alerts, logout confirmation)
 * 2. Call db.open() in useEffect — this triggers the auth flow
 * 3. Route interaction to AuthGateInteraction (exhaustive switch on all 5 types)
 * 4. Render children when DB is open and authenticated
 *
 * Note: Dexie Cloud's ready handler catches auth errors so db.open()
 * can resolve even when not authenticated (e.g. network failures during
 * login). We detect this via currentUserId === "unauthorized" and
 * explicitly call db.cloud.login() to re-trigger the auth flow.
 *
 * NEVER-17: All 5 DXCUserInteraction types handled. logout-confirmation
 *           routes to LogoutConfirmation with Cancel as default.
 * NEVER-09: Loading state shown during db.open(), never empty state.
 */
export function DatabaseProvider({ children, deferInitialSync = false }: DatabaseProviderProps) {
  const { t } = useTranslation()
  // The full-screen loader (and the flag that quiets sync UI while it shows) is skipped when deferring.
  const loaderEnabled = !deferInitialSync
  // Observe Dexie Cloud's request for user interaction
  // Fires for: email, otp, message-alert, logout-confirmation, generic
  const userInteraction = useObservable(db.cloud.userInteraction)

  // Observe current user — drives the auth-complete transition (V1 pattern)
  const currentUser = useObservable(db.cloud.currentUser)

  // Synchronous bridge for auth flow transitions (email → null → otp).
  // Updated during render (not in useEffect) so there's zero flicker frames.
  const lastAuthRef = useRef<DXCUserInteraction | null>(null)

  if (userInteraction != null) {
    lastAuthRef.current = userInteraction
  }

  // Open the database — triggers auth flow via userInteraction observable
  const [isDbOpen, setIsDbOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showSyncLoader, setShowSyncLoader] = useState(false)
  const loaderDismissedRef = useRef(false) // Once dismissed, never re-show
  const bgRunnerRef = useRef<ReturnType<typeof startBackgroundTasks> | undefined>(undefined)
  const mountedRef = useRef(true)
  const viewsToastTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // "Preparing your views..." stays until the sidebar dismisses it, so it must never outlive the
  // provider that raised it: this provider is replaced when the route changes between the app
  // and the consent page (separate DatabaseProvider instances, see App.tsx), and the sidebar
  // may never have mounted. Cancel a toast still waiting to show, and take down one on screen.
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      clearTimeout(viewsToastTimerRef.current)
      toast.dismiss(PREPARING_VIEWS_TOAST_ID)
    }
  }, [])

  // Show a toast while live queries hydrate. Delayed a tick so the Toaster (a child of this
  // provider) has rendered. NOTE: toast.loading() ignores duration in Sonner — use a regular toast.
  const showPreparingViewsToast = useCallback(() => {
    if (!loaderEnabled) return
    clearTimeout(viewsToastTimerRef.current)
    viewsToastTimerRef.current = setTimeout(() => {
      toast(t("Preparing your views..."), { id: PREPARING_VIEWS_TOAST_ID, duration: Infinity })
    }, 100)
  }, [loaderEnabled, t])

  // Start the background runner at most once per mount.
  //
  // Dev only: <StrictMode> (src/index.tsx) runs the mount effect twice
  // (mount -> cleanup -> mount). The cleanup runs before the async
  // dbReadyPromise.then() has started anything, and nothing cancels that
  // first .then(), so both runs started a runner and every background task
  // ran twice, concurrently. Production mounts once — measured: one start,
  // via S1a, in three cold starts — so this guard only makes dev behave like
  // production. It also keeps a stray second call from leaking a runner.
  const startBackgroundTasksOnce = () => {
    if (bgRunnerRef.current) return
    // A startup that finished after this provider unmounted (the route changed meanwhile) would
    // start a runner nobody stops; the provider that replaced it starts its own.
    if (!mountedRef.current) return
    bgRunnerRef.current = startBackgroundTasks()
  }

  // Track whether db.open() resolved (separately from isDbOpen which means "authenticated + ready")
  const [dbOpenResolved, setDbOpenResolved] = useState(false)

  // Instant load: returning users have localStorage placeholder cache.
  // Computed once at mount — if cache exists, skip the loading gate entirely.
  const hasLocalCache = useMemo(() => hasAnyCachedPlaceholder(), [])

  // Sync-aware dismiss: track when initial sync completes + min display time
  const { isReady: isSyncReady, phase: syncPhase } =
    useInitialSyncReady(showSyncLoader)

  // Auto-dismiss InitialSyncLoader when sync is ready
  useEffect(() => {
    if (showSyncLoader && isSyncReady) {
      // Small delay for the fade-out animation to complete, then clear
      // initialSyncActive guard so the sync bar doesn't flash during fade-out.
      const timer = setTimeout(() => {
        setShowSyncLoader(false)
        loaderDismissedRef.current = true
        useAuthStore.getState().setInitialSyncActive(false)

        // Show a toast while live queries hydrate after sync.
        // Sync is already "in-sync" at this point, but useLiveQuery hooks
        // need time to recompute with the new data.
        // Toast stays until sidebar data loads (dismissed by Sidebar.tsx).
        showPreparingViewsToast()
      }, 350)
      return () => clearTimeout(timer)
    }
  }, [showSyncLoader, isSyncReady, showPreparingViewsToast])

  // Early loader: Show InitialSyncLoader immediately when we detect a fresh login
  // is in progress. Two detection methods:
  //
  // 1. URL params (email+otp+otpId) — auto-login via redirect. The addon picks
  //    these up during db.open() and authenticates + syncs. Without this early
  //    detection, the user sees a blank pulsating logo for 30-60s.
  //
  // 2. currentUser transition (unauthorized → authenticated) — manual OTP entry.
  //    The addon emits currentUser during db.open() after OTP is validated.
  //
  // For RETURNING users (cached token), currentUser is already valid on mount
  // and no URL params are present → neither path triggers.
  const hasLoginParams = useRef(
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("otp")
  )
  const initialAuthRef = useRef(
    !!(currentUser?.userId && currentUser.userId !== "unauthorized")
  )
  const wasUnauthRef = useRef(!initialAuthRef.current)

  // On mount: if URL has login params and user is NOT already authenticated,
  // show the loader immediately (before db.open resolves)
  useEffect(() => {
    if (!loaderEnabled) return
    if (initialAuthRef.current) return // Returning user
    if (loaderDismissedRef.current) return
    if (showSyncLoader) return

    if (hasLoginParams.current) {
      setShowSyncLoader(true)
      useAuthStore.getState().setInitialSyncActive(true)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Detect manual OTP login: currentUser transitions unauthorized → authenticated
  useEffect(() => {
    if (!loaderEnabled) return
    if (isDbOpen) return
    if (hasLocalCache) return // Returning user — never show sync loader from this path
    if (loaderDismissedRef.current) return
    if (showSyncLoader) return
    if (initialAuthRef.current) return
    if (hasLoginParams.current) return // Already handled above

    const isAuth = currentUser?.userId && currentUser.userId !== "unauthorized"
    if (!isAuth) {
      wasUnauthRef.current = true
      return
    }

    if (wasUnauthRef.current) {
      wasUnauthRef.current = false
      setShowSyncLoader(true)
      useAuthStore.getState().setInitialSyncActive(true)
    }
  }, [currentUser, isDbOpen, showSyncLoader, hasLocalCache, loaderEnabled])

  // Step 1: Await the module-level db.open() (S7 — already started before React).
  // For returning users with cached tokens, this resolves in ~100ms.
  // For new users, it triggers the auth flow via userInteraction observable.
  useEffect(() => {
    dbReadyPromise
      .then(async () => {
        setDbOpenResolved(true)
        // db.open() resolved — check if user is already authenticated (cached token).
        // Returning users: currentUserId is set immediately from cached auth.
        const userId = db.cloud.currentUserId
        const isAuthenticated = userId && userId !== "unauthorized"

        if (isAuthenticated) {
          setIsDbOpen(true)

          // Check if this is a true returning user (has synced data) or a fresh
          // login via URL params where db.open() resolved with auth but sync
          // hasn't completed yet. syncState.phase === "in-sync" means data is
          // available locally; anything else means we need to wait for sync.
          const syncPhase = db.cloud.syncState?.value?.phase
          const hasLocalData = (await db.totodo.count()) > 0
          const hasSyncedData = syncPhase === "in-sync" || hasLocalData

          if (hasSyncedData) {
            // S1a: Data is local, show UI immediately.
            // If no localStorage cache exists, this is a fresh login where
            // db.open() blocked until sync completed (manual OTP flow).
            // Show a toast so the user knows views are hydrating.
            // Toast stays until sidebar data loads (dismissed by Sidebar.tsx).
            if (!hasLocalCache && !loaderDismissedRef.current) {
              showPreparingViewsToast()
            }
            // Populate for new users: sync completed fast (empty account) so
            // we landed here instead of S1b. Safe to call — guards inside
            // populateIfNeeded() check #populated + totodo.count().
            if (syncPhase === "in-sync") {
              void populateIfNeeded()
            }
            setTimeout(() => initDocProcessing(), 10_000)
            startBackgroundTasksOnce()
          } else {
            // S1b: AUTHENTICATED BUT NOT SYNCED — fresh login via URL params
            // or Dexie.delete was blocked and data is stale. Show loader until
            // sync completes so the user sees progress, not empty skeletons.
            // Skip for returning users with localStorage cache — they see cached data.
            const isCrashRecovery = consumeRecoveryState() !== null

            if (loaderEnabled && !isCrashRecovery && !loaderDismissedRef.current && !hasLocalCache) {
              setShowSyncLoader(true)
              useAuthStore.getState().setInitialSyncActive(true)
            }

            void (async () => {
              await waitForInitialSync(db)

              const phase = db.cloud.syncState?.value?.phase
              if (phase === "in-sync") {
                await populateIfNeeded()
              }

              setTimeout(() => initDocProcessing(), 10_000)
              startBackgroundTasksOnce()
            })()
          }
        }
        // New/logged-out users: currentUser observable will fire → handled in Step 2
      })
      .catch((e) => {
        console.error("[DB] Open error:", e)
        setError(e instanceof Error ? e.message : String(e))
      })

    return () => {
      bgRunnerRef.current?.stop()
      bgRunnerRef.current = undefined
    }
  }, [])

  // Track sync errors for analytics.
  useEffect(() => {
    const sub = db.cloud.syncState.subscribe((state) => {
      if (state?.error) {
        const err = state.error
        console.error("[SYNC] syncState error:", err?.message, err?.stack)
        import("@/shared/services/analyticsTrackers").then(
          ({ trackSyncFailed }) => trackSyncFailed(err?.message ?? "unknown"),
        ).catch(() => {})
      }
    })
    return () => sub.unsubscribe()
  }, [])

  // Step 2: Handle NEW users — first login after OTP/OAuth completes.
  // Returning users are already handled in Step 1 (S1 fast path).
  // This fires when Dexie Cloud's currentUser observable changes to authenticated.
  //
  // RACE CONDITION FIX: We must wait for dbOpenResolved before making decisions.
  // Without this, useObservable(db.cloud.currentUser) returns the cached user
  // synchronously (BehaviorSubject) BEFORE Step 1's .then() fires — causing
  // the loader to show for returning users on every page refresh.
  useEffect(() => {
    if (isDbOpen) return // Already open (returning user fast path)
    if (!dbOpenResolved) return // Wait for db.open() to finish — Step 1 handles returning users
    if (!currentUser?.userId || currentUser.userId === "unauthorized") return


    setIsDbOpen(true)

    // Show InitialSyncLoader on fresh login (Step 2 = new auth session).
    // Returning users with cached tokens hit Step 1 instead, so this only
    // fires after OTP/OAuth completes. Even if stale IDB data survived a
    // blocked Dexie.delete, the loader gives clear feedback that initial
    // sync is in progress. It auto-dismisses when isSyncReady fires.
    // Skip for returning users who have local cache — they see the app immediately.
    const isCrashRecovery = consumeRecoveryState() !== null
    if (loaderEnabled && !isCrashRecovery && !loaderDismissedRef.current && !hasLocalCache) {
      setShowSyncLoader(true)
      useAuthStore.getState().setInitialSyncActive(true)
    }

    // New user startup: MUST wait for initial sync before populate
    // (prevents duplicate tutorials when #populated hasn't synced yet)
    void (async () => {
      await waitForInitialSync(db)

      // Only populate if sync actually completed ("in-sync").
      // If sync failed ("error") or went offline, the user might be an
      // existing user whose data hasn't arrived — populating would create
      // a duplicate tutorial project on their account.
      const phase = db.cloud.syncState?.value?.phase
      if (phase === "in-sync") {
        await populateIfNeeded()
      } else {
        console.warn(`[DatabaseProvider] Skipping populate — sync phase is "${phase}", not "in-sync"`)

        // Recovery watcher: Dexie Cloud retries sync automatically (20s, 40s,
        // 5min, 15min backoff). When it eventually succeeds, run populate for
        // brand-new users who would otherwise see an empty app forever.
        const sub = db.cloud.syncState.subscribe(
          (state: { phase: string }) => {
            if (state.phase === "in-sync") {
              sub.unsubscribe()
              console.info("[DatabaseProvider] Sync recovered — running deferred populate")
              void populateIfNeeded()
            }
          },
        )
      }

      setTimeout(() => initDocProcessing(), 10_000)
      startBackgroundTasksOnce()
    })()
  }, [currentUser, isDbOpen, dbOpenResolved])

  // Clear bridge ref once DB is open (auth succeeded, transition to app)
  useEffect(() => {
    if (isDbOpen) {
      lastAuthRef.current = null
    }
  }, [isDbOpen])

  // Step 3: Detect stuck state — db opened but not authenticated and no interaction.
  // This happens when Dexie Cloud is down: db.open() resolves, user is "unauthorized",
  // message-alert is shown and dismissed, but no "email" interaction re-fires.
  // Re-trigger the auth flow after a short delay to bring back the sign-in form.
  useEffect(() => {
    if (isDbOpen || !dbOpenResolved) return // Already authenticated or db not ready yet
    if (userInteraction != null) return // Active interaction — don't interfere
    if (error != null) return // Error screen already showing

    const userId = db.cloud.currentUserId
    if (userId && userId !== "unauthorized") return // Authenticated, waiting for Step 2

    // We're stuck: db is open, user is unauthorized, no interaction.
    // Re-trigger login after a short delay (avoid racing with normal auth flow transitions)
    const timer = setTimeout(() => {
      // Double-check we're still stuck (interaction may have fired during delay)
      if (db.cloud.currentUserId === "unauthorized") {
        void db.cloud.login()
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [isDbOpen, dbOpenResolved, userInteraction, error])

  // Determine what to display: real interaction, or bridge during null gap.
  // Bridge only applies during the email→null→otp transition (brief null gap).
  // After OTP succeeds, userInteraction goes null — don't bridge, let it
  // fall through to the loading bar while currentUser resolves.
  const bridgeInteraction =
    !isDbOpen && lastAuthRef.current?.type === "email"
      ? lastAuthRef.current
      : null

  const displayInteraction = userInteraction ?? bridgeInteraction

  // Signed in with a session token — enough for a deferInitialSync route, even while db.open()
  // is still waiting for the first download.
  const hasSession =
    !!currentUser?.userId && currentUser.userId !== "unauthorized" && !!currentUser.accessToken

  // Dexie Cloud wants user interaction (login, OTP, re-auth, alerts, logout)
  // This can happen before db.open() resolves (first launch / logged out)
  // or after (re-authentication needed, license limit, logout confirmation)
  //
  // PRE-AUTH (email, otp): Full-page replacement — no app to show yet.
  // POST-AUTH (message-alert, logout-confirmation, generic): Modal overlay
  // on top of the running app so the user doesn't lose context.
  const isPostAuthInteraction =
    isDbOpen &&
    displayInteraction != null &&
    displayInteraction.type !== "email" &&
    displayInteraction.type !== "otp"

  if (displayInteraction != null && !isPostAuthInteraction) {
    // Auth is gone — clear any cached placeholder data so stale user
    // content doesn't flash on next reload. The cache rebuilds on login.
    clearUserLocalStorage()
    return <AuthGateInteraction interaction={displayInteraction} />
  }

  // Error during open or authentication
  if (error != null) {
    return (
      <div className="flex h-screen items-center justify-center p-4">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-destructive">
            {t("Database Error")}
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 text-sm text-primary hover:underline"
          >
            {t("Try again")}
          </button>
        </div>
      </div>
    )
  }

  // DB is opening, no user interaction yet — show branded loading (NEVER-09)
  // If stuck for too long (>10s), show a connection error with retry.
  if (!isDbOpen) {
    // Returning user with cached data — render children immediately.
    // useLiveQueryImmediate hooks will show localStorage placeholders
    // while db.open() resolves in background (~100ms).
    // NOTE: Don't check showSyncLoader here — there's a race where
    // currentUser observable fires (unauthorized→auth) before Step 1's
    // .then() sets isDbOpen=true, causing showSyncLoader to be set
    // incorrectly for returning users.
    if (hasLocalCache || (deferInitialSync && hasSession)) {
      return (
        <Suspense
          fallback={
            <div className="flex h-screen items-center justify-center">
              <div className="text-muted-foreground text-sm">{t("Loading...")}</div>
            </div>
          }
        >
          {children}
        </Suspense>
      )
    }

    // Fresh login detected — show sync loader with progress
    if (showSyncLoader) {
      return (
        <InitialSyncLoader
          phase={syncPhase}
          isFadingOut={isSyncReady}
        />
      )
    }
    return (
      <LoadingWithTimeout
        dbOpenResolved={dbOpenResolved}
        onRetry={() => void db.cloud.login()}
      />
    )
  }

  // DB is open + authenticated — render the app (or sync loader exclusively)
  if (showSyncLoader) {
    return (
      <InitialSyncLoader
        phase={syncPhase}
        isFadingOut={isSyncReady}
      />
    )
  }

  return (
    <Suspense
      fallback={
        <div className="flex h-screen items-center justify-center">
          <div className="text-muted-foreground text-sm">{t("Loading...")}</div>
        </div>
      }
    >
      {children}
      {/* Post-auth modal overlay for message-alert / logout-confirmation / generic */}
      {isPostAuthInteraction && displayInteraction && (
        <InAppInteractionOverlay interaction={displayInteraction} />
      )}
    </Suspense>
  )
}
